import React from "react";

import cx from "classnames";

import { coalesce } from "@kazoohr/helpers";

import styles from "./TabIndexVisibility.scss";

const tabIndexableTags = ["a", "button", "input", "select", "textarea"];

export interface TabIndexVisibilityProps {
  children?: React.ReactNode;
  /**
   * Default: true. If false, adds `tabIndex: -1` to all children that require it.
   */
  visible?: boolean;
  /**
   * Optional `className` to provide the `div` element
   */
  className?: string;
}

let ids = 0;

/**
 * An accessibility helper component to remove all of the children of this
 * component from the tab index tree by adding `tabIndex: -1` to their props if
 * they are a tabbable element _or_ have a non-zero tab index.
 *
 * Wraps the component tree in a `div` element for controlling tab indexes.
 */
export function TabIndexVisibility({
  visible = true,
  children = [],
  className = "",
}: TabIndexVisibilityProps) {
  const id = React.useMemo(() => `tab-indexer-${ids++}`, []);

  React.useEffect(() => {
    document.querySelectorAll(`#${id} *`).forEach((element) => {
      if (
        !(element instanceof HTMLElement) ||
        !elementSupportsTabIndex(element)
      ) {
        return;
      }
      if (visible) {
        element.tabIndex = parseInt(
          coalesce(element.dataset.originalTabIndex, "0"),
          10
        );
      } else {
        element.dataset.oldTabIndex = String(
          element.dataset.oldTabIndex || element.tabIndex || 0
        );
        element.tabIndex = -1;
      }
    });
  }, [visible, id]);

  return (
    <div id={id} className={cx(styles.tabIndexVisibility, className)}>
      {children}
    </div>
  );
}

function elementSupportsTabIndex(element: HTMLElement): boolean {
  if (tabIndexableTags.includes(element.nodeName.toLowerCase())) {
    return true;
  }
  return false;
}
